// src/components/VideoEngine/Templates/MSN_Landscape/Sequences/Intro.js

import React from "react";
import {
  AbsoluteFill,
  Sequence,
  Img,
  OffthreadVideo, // <--- REPLACED 'Video'
  Audio,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
  Easing,
} from "remotion";
import { TeaserCarousel } from "../../../Common/TeaserCarousel";
import { KaraokeCaptions } from "../../../Common/KaraokeCaptions";

export const IntroSlide = ({
  item,
  theme,
  brand,
  teaserImages = [],
  backgroundVideo,
  ENABLE_AUDIO = true,
}) => {
  const { fps } = useVideoConfig();
  const frame = useCurrentFrame();

  // --- 1. TIMING: The Two-Stage Timeline ---
  const visualDelay = 20;
  const brandingEnter = spring({
    frame: frame - visualDelay,
    fps,
    config: { damping: 20, stiffness: 100 } // <--- CRITICALLY DAMPED (No bounce)
  });

  const audioDelay = item.audioDelay || 0;
  const karaokeEnter = spring({
    frame: frame - audioDelay,
    fps,
    config: { damping: 20, stiffness: 100 } // <--- CRITICALLY DAMPED (No bounce)
  });

  // Link Video Blur to Branding Entry
  const videoBlur = interpolate(brandingEnter, [0, 1], [0, 3]);
  const videoBrightness = interpolate(brandingEnter, [0, 1], [1, 0.6]);

  // --- 2. EXIT ANIMATION ---
  const exitScale = interpolate(
    frame,
    [item.durationInFrames - 15, item.durationInFrames],
    [1, 15],
    { extrapolateLeft: "clamp", easing: Easing.in(Easing.poly(3)) }
  );
  const pan = interpolate(frame, [0, 150], [0, -20]);

  // --- 3. DYNAMIC SOURCE SELECTION ---
  const videoSrc = backgroundVideo || "https://www.pexels.com/download/video/6003440/";

  return (
    <AbsoluteFill style={{ backgroundColor: "#0f172a" }}>
      {ENABLE_AUDIO && item.props?.audioUrl && (
        <Sequence from={item.audioDelay || 0}>
          <Audio src={item.props.audioUrl} />
        </Sequence>
      )}

      {/* BACKGROUND LAYER */}
      <AbsoluteFill style={{ zIndex: 0 }}>
        <OffthreadVideo 
          src={videoSrc}
          muted={true} // <--- THE FIX: Explicitly silence the audio track
          style={{
            width: "100%",
            height: "120%",
            objectFit: "cover",
            transform: `translateY(${pan}px)`,
            filter: `brightness(${videoBrightness}) blur(${videoBlur}px)`,
          }}
          onError={(e) => console.warn("Video failed", e)}
        />
      </AbsoluteFill>

      {/* EDITORIAL SPLIT OVERLAY */}
      <AbsoluteFill style={{
        background: `linear-gradient(90deg, ${theme.accent} 0%, ${theme.accent} 25%, transparent 50%)`,
        opacity: 0.85,
        zIndex: 0
      }} />

      {/* MAIN CONTENT */}
      <AbsoluteFill
        style={{
          zIndex: 1,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px",
          gap: "60px",
        }}
      >
        {/* LEFT COL: TYPOGRAPHY */}
        <div style={{
          flex: 1.2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          //justifyContent: 'center', // Visual Center
          paddingTop: 130, // <--- Hard pixel offset from top - sets the fixed positioning!
          height: '100%', // <--- FIX 1: Lock height to prevent "Center of Mass" shift
          // paddingTop: 80 // Removed in favor of flex centering within the locked height
        }}>

          {/* Logo Watermark */}
          <div style={{
            marginBottom: 20,
            opacity: brandingEnter,
            flexShrink: 0, // <--- FIX 2: Prevent squishing
            willChange: "transform, opacity" // <--- GPU ACCELERATION
          }}>
            <Img
              src="https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Floveinc.png?alt=media&token=22866cd2-1b3b-4c74-9e24-762e6cfd2c33"
              style={{ height: 127, width: 'auto' }}
            />
          </div>

          <div
            style={{
              opacity: brandingEnter,
              transform: `translateY(${(1 - brandingEnter) * 20}px)`,
              backgroundColor: theme.primary,
              color: "white",
              padding: "8px 20px",
              borderRadius: "8px",
              fontSize: 28,
              fontWeight: 700,
              fontFamily: theme.fontMain,
              marginBottom: 20,
              letterSpacing: 2,
              display: 'inline-block',
              flexShrink: 0,
              willChange: "transform, opacity" // <--- GPU ACCELERATION
            }}
          >
            THE EDIT
          </div>

          <h1
            style={{
              opacity: brandingEnter,
              transform: `translateY(${(1 - brandingEnter) * 40}px)`,
              fontFamily: theme.fontDisplay || theme.fontMain,
              color: "white",
              fontSize: 110,
              lineHeight: 0.9,
              margin: 0,
              marginBottom: 30,
              textTransform: "uppercase",
              textAlign: "left",
              textShadow: "0 4px 20px rgba(0,0,0,0.5)",
              flexShrink: 0,
              willChange: "transform, opacity" // <--- GPU ACCELERATION
            }}
          >
            {item.text || "TITLE HERE"}
          </h1>

          <div style={{
            borderTop: `2px solid ${theme.secondary}`,
            paddingTop: 20,
            width: '100%',
            opacity: brandingEnter,
            transform: `translateY(${(1 - brandingEnter) * 20}px)`,
            flexShrink: 0,
            willChange: "transform, opacity" // <--- GPU ACCELERATION
          }}>
            <h3
              style={{
                opacity: brandingEnter,
                transform: `translateY(${(1 - brandingEnter) * 60}px)`,
                color: theme.secondary,
                fontFamily: theme.fontMain,
                fontWeight: 400,
                letterSpacing: 4,
                margin: 0,
                fontSize: 32,
                willChange: "transform, opacity" // <--- GPU ACCELERATION
              }}
            >
              LOVE{brand ? brand.toUpperCase() : "MONEY"}
            </h3>
          </div>

          {/* KARAOKE INJECTION */}
          {item.captions && item.captions.length > 0 && (
            <div
              style={{
                opacity: karaokeEnter,
                transform: `translateY(${(1 - karaokeEnter) * 80}px)`,
                marginTop: 35,
                width: '100%',
                padding: "10px 0px",
                display: "flex",
                alignItems: "flex-start", // <--- FIX 3: Top Anchor (Growth goes DOWN)
                height: 180, // <--- FIX 4: Reserved Space (Prevents layout reflow)
                flexShrink: 0,
                willChange: "transform, opacity" // <--- GPU ACCELERATION
              }}
            >
              <div style={{ fontSize: 46, fontFamily: theme.fontMain }}>
                <KaraokeCaptions
                  captions={item.captions}
                  theme={theme}
                  delay={item.audioDelay || 0}
                />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COL: TEASER CAROUSEL */}
        <div style={{
          flex: 0.5,
          height: "70%",
          zIndex: exitScale > 1.1 ? 100 : 1,
          transform: `translateX(${(1 - karaokeEnter) * 50}px) scale(${exitScale})`,
          opacity: karaokeEnter,
          willChange: "transform, opacity" // <--- GPU ACCELERATION
        }}>
          <TeaserCarousel
            images={teaserImages}
            duration={item.durationInFrames}
            theme={theme}
            delay={audioDelay}
          />
        </div>

      </AbsoluteFill>
    </AbsoluteFill>
  );
};